import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import './AdminDashboard.css';

function AddMatch() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    team1: '',
    team2: '',
    matchDate: '',
    matchTime: '',
    status: 'upcoming'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Submit form to create new match
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prevent double submission
    if (loading || isSubmitted) {
      return;
    }

    setLoading(true);
    setError(null);
    setIsSubmitted(true);

    // Show loading toast
    const loadingToastId = toast.loading("Creating match...");

    try {
      // Check all matches first, not just upcoming
      try {
        const checkResponse = await fetch(`${process.env.REACT_APP_BASE_URL}/api/match-setup`);
        if (checkResponse.ok) {
          const data = await checkResponse.json();
          
          if (data.success && data.data && data.data.length > 0) {
            // Check for matches with the same teams and date/time
            const existingMatch = data.data.find(match => 
              (match.team1 === formData.team1 && 
               match.team2 === formData.team2 && 
               match.matchDate === formData.matchDate &&
               match.matchTime === formData.matchTime) ||
              (match.team1 === formData.team2 && 
               match.team2 === formData.team1 && 
               match.matchDate === formData.matchDate &&
               match.matchTime === formData.matchTime)
            );
            
            if (existingMatch) {
              // If match exists, update toast and navigate to edit that match
              toast.update(loadingToastId, { 
                render: "Match already exists! Redirecting to edit...", 
                type: "info", 
                isLoading: false,
                autoClose: 2000
              });
              
              setTimeout(() => {
                navigate('/admin/match-setup', { state: { match: existingMatch } });
              }, 2000);
              
              return;
            }
          }
        }
      } catch (checkError) {
        console.log("Error checking for existing matches:", checkError);
        // Continue with match creation even if the check fails
      }
      
      // Create a new unique match ID (timestamp-based for simplicity)
      const matchId = Date.now().toString();
      
      const matchData = {
        matchId,
        team1: formData.team1,
        team2: formData.team2,
        matchDate: formData.matchDate,
        matchTime: formData.matchTime,
        status: formData.status,
        // Initialize empty questions and parlayRules arrays
        questions: [],
        parlayRules: []
      };

      console.log('Sending match data:', matchData);

      // Make the request with minimal headers to avoid CORS issues
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/match-setup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(matchData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create match');
      }

      const result = await response.json();
      
      // Check if the response indicates it's an update rather than a new creation
      const isUpdate = result.message && result.message.includes('update');
      
      // Update toast with appropriate message
      toast.update(loadingToastId, { 
        render: result.message || (isUpdate ? "Match updated successfully!" : "Match created successfully!"), 
        type: "success", 
        isLoading: false,
        autoClose: 2000
      });

      // Wait for 2 seconds before navigating
      setTimeout(() => {
        // If successful, navigate to match setup to configure questions and parlay rules
        navigate('/admin/match-setup', { 
          state: { 
            match: result.data 
          } 
        });
      }, 2000);
    } catch (err) {
      console.error('Error creating match:', err);
      setError(err.message || 'Failed to create match');
      setIsSubmitted(false); // Reset submitted state to allow retry
      
      // Update toast with error message
      toast.update(loadingToastId, { 
        render: `Failed to create match: ${err.message || 'Unknown error'}`, 
        type: "error", 
        isLoading: false,
        autoClose: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-dashboard">
      <header className="dashboard-header">
        <div className="header-content">
          <h1>Add New Match</h1>
          <button 
            onClick={() => navigate('/admin/dashboard')} 
            className="back-button"
            disabled={loading}
          >
            Back to Dashboard
          </button>
        </div>
      </header>

      <main className="dashboard-content">
        <div className="form-container">
          <form onSubmit={handleSubmit} className="match-form">
            {error && <div className="error-message">{error}</div>}
            
            <div className="form-group">
              <label htmlFor="team1">Team 1</label>
              <input
                type="text"
                id="team1"
                name="team1"
                value={formData.team1}
                onChange={handleChange}
                required
                placeholder="Enter team 1 name"
                disabled={loading || isSubmitted}
              />
            </div>

            <div className="form-group">
              <label htmlFor="team2">Team 2</label>
              <input
                type="text"
                id="team2"
                name="team2"
                value={formData.team2}
                onChange={handleChange}
                required
                placeholder="Enter team 2 name"
                disabled={loading || isSubmitted}
              />
            </div>

            <div className="form-group">
              <label htmlFor="matchDate">Match Date</label>
              <input
                type="date"
                id="matchDate"
                name="matchDate"
                value={formData.matchDate}
                onChange={handleChange}
                required
                disabled={loading || isSubmitted}
              />
            </div>

            <div className="form-group">
              <label htmlFor="matchTime">Match Time</label>
              <input
                type="time"
                id="matchTime"
                name="matchTime"
                value={formData.matchTime}
                onChange={handleChange}
                required
                disabled={loading || isSubmitted}
              />
            </div>

            <div className="form-group">
              <label htmlFor="status">Status</label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleChange}
                disabled={loading || isSubmitted}
              >
                <option value="upcoming">Upcoming</option>
                <option value="live">Live</option>
              </select>
            </div>

            <div className="form-actions">
              <button 
                type="submit" 
                className="submit-button"
                disabled={loading || isSubmitted}
              >
                {loading ? 'Creating...' : isSubmitted ? 'Submitted...' : 'Create Match'}
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}

export default AddMatch; 